/**
 * Tenant contact + social links (landing settings).
 * Shared FE/BE — href builders and presence for card / footer / floating.
 */

/** @typedef {'phone' | 'whatsapp' | 'email' | 'instagram' | 'facebook' | 'tiktok' | 'youtube' | 'telegram' | 'pinterest' | 'website'} ContactLinkId */

export const CONTACT_LINK_IDS = [
  'phone',
  'whatsapp',
  'email',
  'instagram',
  'facebook',
  'tiktok',
  'youtube',
  'telegram',
  'pinterest',
  'website',
];

export const SOCIAL_LINK_IDS = [
  'instagram',
  'facebook',
  'tiktok',
  'youtube',
  'telegram',
  'pinterest',
  'website',
];

export const FLOATING_PLACEMENT_IDS = ['menu', 'left', 'right', 'bottom', 'top'];

export const FLOATING_STYLE_IDS = ['icon', 'labeled'];

export const FOOTER_CONTACT_STYLE_IDS = ['strict', 'buttons'];

/** Default footer look: classic text list + social icons column. */
export const DEFAULT_FOOTER_CONTACT_STYLE = 'strict';

/**
 * Storefront chrome for contacts / floating / footer menu (travels in UI presets).
 * Personal values (phone, social URLs) stay on landing.
 *
 * @param {unknown} raw
 */
export function normalizeContactChrome(raw) {
  const source = asRecord(raw);
  return {
    footerContactStyle: normalizeFooterContactStyle(
      source.footerContactStyle ?? source.footer_contact_style,
    ),
    floatingStyle: normalizeFloatingStyle(source.floatingStyle ?? source.floating_style),
    floatingPlacements: normalizeFloatingPlacements(
      source.floatingPlacements ?? source.floating_placements,
    ),
    linkPresence: normalizeLinkPresence(source.linkPresence ?? source.link_presence),
    showMenuInFooter: asBool(
      source.showMenuInFooter ?? source.show_menu_in_footer,
      false,
    ),
  };
}

/**
 * Seed storefront chrome from legacy landing fields when contactChrome was never saved.
 *
 * @param {unknown} moduleSettingsRaw
 * @param {unknown} landingRaw
 */
export function resolveContactChrome(moduleSettingsRaw, landingRaw) {
  const ms = asRecord(moduleSettingsRaw);
  if (ms.contactChrome != null || ms.contact_chrome != null) {
    return normalizeContactChrome(ms.contactChrome ?? ms.contact_chrome);
  }
  const landing = asRecord(landingRaw);
  return normalizeContactChrome({
    footerContactStyle: landing.footerContactStyle ?? landing.footer_contact_style,
    floatingStyle: landing.floatingStyle ?? landing.floating_style,
    floatingPlacements: landing.floatingPlacements ?? landing.floating_placements,
    linkPresence: landing.linkPresence ?? landing.link_presence,
    showMenuInFooter: false,
  });
}

/** @type {Record<string, { card: boolean, footer: boolean, floating: boolean }>} */
export const DEFAULT_LINK_PRESENCE = {
  phone: { card: true, footer: true, floating: true },
  whatsapp: { card: true, footer: true, floating: true },
  email: { card: true, footer: true, floating: false },
  instagram: { card: true, footer: true, floating: true },
  facebook: { card: true, footer: true, floating: false },
  tiktok: { card: true, footer: true, floating: false },
  youtube: { card: true, footer: true, floating: false },
  telegram: { card: true, footer: true, floating: true },
  pinterest: { card: true, footer: true, floating: false },
  website: { card: true, footer: true, floating: false },
};

export const DEFAULT_FLOATING_PLACEMENTS = {
  menu: false,
  left: false,
  right: false,
  bottom: false,
  top: false,
};

/**
 * Resolve left/right/top/bottom checkboxes into render slots.
 * - one edge → center of that edge
 * - adjacent pair → one corner
 * - opposite pair → two edge centers (copies)
 * - three edges → two corners on the shared side
 * - all four → four edge centers
 * Menu is not part of this set.
 *
 * @param {Partial<Record<'left'|'right'|'top'|'bottom', boolean>> | null | undefined} placements
 * @returns {Array<'left'|'right'|'top'|'bottom'|'top-left'|'top-right'|'bottom-left'|'bottom-right'>}
 */
export function resolveFloatingEdgeSlots(placements) {
  const source = asRecord(placements);
  const left = asBool(source.left, false);
  const right = asBool(source.right, false);
  const top = asBool(source.top, false);
  const bottom = asBool(source.bottom, false);
  const count = [left, right, top, bottom].filter(Boolean).length;

  if (count === 0) return [];
  if (count === 4) return ['left', 'right', 'top', 'bottom'];
  if (count === 1) {
    if (left) return ['left'];
    if (right) return ['right'];
    if (top) return ['top'];
    return ['bottom'];
  }
  if (count === 2) {
    if (left && right) return ['left', 'right'];
    if (top && bottom) return ['top', 'bottom'];
    if (top && left) return ['top-left'];
    if (top && right) return ['top-right'];
    if (bottom && left) return ['bottom-left'];
    if (bottom && right) return ['bottom-right'];
  }
  // Three edges → two corners sharing the single perpendicular side.
  if (left && right && top) return ['top-left', 'top-right'];
  if (left && right && bottom) return ['bottom-left', 'bottom-right'];
  if (top && bottom && left) return ['top-left', 'bottom-left'];
  if (top && bottom && right) return ['top-right', 'bottom-right'];
  return [];
}

export const EMPTY_SOCIAL_LINKS = {
  instagram: '',
  facebook: '',
  tiktok: '',
  youtube: '',
  telegram: '',
  pinterest: '',
  website: '',
};

function asRecord(raw) {
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? /** @type {Record<string, unknown>} */ (raw)
    : {};
}

function asString(raw) {
  return String(raw ?? '').trim();
}

function asBool(raw, fallback) {
  if (raw === true || raw === 'true' || raw === 1 || raw === '1') return true;
  if (raw === false || raw === 'false' || raw === 0 || raw === '0') return false;
  return fallback;
}

/**
 * @param {unknown} raw
 */
export function normalizeSocialLinks(raw) {
  const source = asRecord(raw);
  /** @type {Record<string, string>} */
  const out = { ...EMPTY_SOCIAL_LINKS };
  for (const id of SOCIAL_LINK_IDS) {
    out[id] = asString(source[id]);
  }
  return out;
}

/**
 * @param {unknown} raw
 */
export function normalizeLinkPresence(raw) {
  const source = asRecord(raw);
  /** @type {Record<string, { card: boolean, footer: boolean, floating: boolean }>} */
  const out = {};
  for (const id of CONTACT_LINK_IDS) {
    const block = asRecord(source[id]);
    const d = DEFAULT_LINK_PRESENCE[id];
    out[id] = {
      card: asBool(block.card, d.card),
      footer: asBool(block.footer, d.footer),
      floating: asBool(block.floating, d.floating),
    };
  }
  return out;
}

/**
 * @param {unknown} raw
 */
export function normalizeFloatingPlacements(raw) {
  const source = asRecord(raw);
  /** @type {Record<string, boolean>} */
  const out = { ...DEFAULT_FLOATING_PLACEMENTS };
  for (const id of FLOATING_PLACEMENT_IDS) {
    out[id] = asBool(source[id], DEFAULT_FLOATING_PLACEMENTS[id]);
  }
  return out;
}

/**
 * @param {unknown} raw
 * @returns {'icon' | 'labeled'}
 */
export function normalizeFloatingStyle(raw) {
  const s = asString(raw);
  return FLOATING_STYLE_IDS.includes(s) ? /** @type {'icon' | 'labeled'} */ (s) : 'icon';
}

/**
 * @param {unknown} raw
 * @returns {'strict' | 'buttons'}
 */
export function normalizeFooterContactStyle(raw) {
  const s = asString(raw);
  return FOOTER_CONTACT_STYLE_IDS.includes(s)
    ? /** @type {'strict' | 'buttons'} */ (s)
    : DEFAULT_FOOTER_CONTACT_STYLE;
}

/**
 * @param {string} value
 */
export function whatsappDigits(value) {
  return asString(value).replace(/\D/g, '');
}

/**
 * @param {string} value
 * @param {'instagram' | 'telegram' | 'tiktok' | 'facebook' | 'youtube' | 'pinterest' | 'website'} kind
 */
export function normalizeSocialHref(value, kind) {
  const raw = asString(value);
  if (!raw) return '';

  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('//')) return `https:${raw}`;

  const handle = raw.replace(/^@/, '').replace(/^\//, '');

  switch (kind) {
    case 'instagram':
      return `https://instagram.com/${handle}`;
    case 'telegram':
      return `https://t.me/${handle}`;
    case 'tiktok':
      return handle.includes('.') ? `https://${handle}` : `https://www.tiktok.com/@${handle}`;
    case 'facebook':
      return handle.includes('.') ? `https://${handle}` : `https://facebook.com/${handle}`;
    case 'youtube':
      if (handle.startsWith('watch') || handle.startsWith('channel') || handle.startsWith('@') || handle.startsWith('c/')) {
        return `https://www.youtube.com/${handle.replace(/^@/, '@')}`;
      }
      return handle.includes('.') ? `https://${handle}` : `https://www.youtube.com/@${handle}`;
    case 'pinterest':
      return handle.includes('.') ? `https://${handle}` : `https://pinterest.com/${handle}`;
    case 'website':
      return `https://${handle}`;
    default:
      return raw;
  }
}

/**
 * @param {{
 *   phone?: string,
 *   email?: string,
 *   whatsapp?: string,
 *   social?: Record<string, string>,
 *   presence?: Record<string, { card?: boolean, footer?: boolean, floating?: boolean }>,
 *   surface: 'card' | 'footer' | 'floating',
 * }} opts
 * @returns {Array<{ id: string, href: string, external: boolean }>}
 */
export function buildContactLinkActions(opts) {
  const presence = normalizeLinkPresence(opts.presence);
  const social = normalizeSocialLinks(opts.social);
  const surface = opts.surface;
  /** @type {Array<{ id: string, href: string, external: boolean }>} */
  const actions = [];

  const pushIf = (id, href, external) => {
    if (!href) return;
    if (!presence[id]?.[surface]) return;
    actions.push({ id, href, external });
  };

  const phone = asString(opts.phone);
  const email = asString(opts.email);
  const wa = whatsappDigits(opts.whatsapp || '');

  pushIf('phone', phone ? `tel:${phone}` : '', false);
  pushIf('whatsapp', wa ? `https://wa.me/${wa}` : '', true);
  pushIf('email', email ? `mailto:${email}` : '', false);

  for (const id of SOCIAL_LINK_IDS) {
    const href = normalizeSocialHref(social[id], /** @type {any} */ (id));
    pushIf(id, href, true);
  }

  return actions;
}
